import type { ImportDataActionStatus } from "#src/batteries-included-components/Buttons/ImportDataAction/ImportDataActionContext/types";
import { useAuthenticatedContext } from "#src/contexts/AuthenticatedContext.helpers";
import { useQueueUnique } from "#src/hooks/useQueue";
import { ExceptionUtils } from "#src/utils/exception";
import {
  uploadFileForDatasetImport,
  type DatasetFileImportRegistrationType,
} from "#src/utils/datasetFileImport";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Dialog,
  DropdownInput,
  FileInput,
  Form,
  useForm,
  useToast,
  type DialogProps,
} from "@validereinc/common-components";
import {
  DatasetAdapter,
  Resources,
  TemplateAdapter,
  TransactionAdapter,
  type TransactionType,
} from "@validereinc/domain";
import { downloadBlob, toTitleCaseString } from "@validereinc/utilities";
import { snakeCase } from "es-toolkit/compat";
import mime from "mime";
import React, { useRef, useState } from "react";
import { z } from "zod";
import { getDataSetImportDataActionQueueKey } from "./DataSetImportDataDialog.helpers";

const queueKey = getDataSetImportDataActionQueueKey();
const acceptedMimeTypes = [
  mime.getType("csv"),
  mime.getType("xls"),
  mime.getType("xlsx"),
  mime.getType("json"),
].join(",");
const DataSetImportDialogForm = z.object({
  dataset: z.string().uuid(),
  file: z
    .array(z.instanceof(File))
    .refine((files) => files.every((f) => acceptedMimeTypes.includes(f.type)), {
      message: "Invalid file type",
    }),
});
type DataSetImportDialogFormType = z.infer<typeof DataSetImportDialogForm>;

export const DataSetImportDataDialog = ({
  isOpen,
  onClose,
}: Pick<DialogProps, "isOpen" | "onClose">) => {
  const form = useForm<DataSetImportDialogFormType>();
  const queryClient = useQueryClient();
  const selectedDataset = form.watch("dataset");

  const [queue, setQueue] = useQueueUnique<TransactionType>(
    [],
    queueKey,
    (tx) => tx.transaction_id
  );
  const [status, setStatus] = useState<ImportDataActionStatus>();
  const { toast } = useToast();
  const { claims } = useAuthenticatedContext();

  const datasetQueryPayload: Parameters<typeof DatasetAdapter.getList>[0] = {
    filters: {
      company_id: claims.company?.id ?? "",
      status: true, // REVIEW: this filter doesn't work so the status must be shown where needed
    },
  };
  const { data: datasets, isLoading: areDatasetsLoading } = useQuery({
    queryKey: ["dataset", "list", datasetQueryPayload],
    queryFn: () => DatasetAdapter.getList(datasetQueryPayload),
    enabled: Boolean(claims.company?.id),
  });
  const { data: templateBlob, isLoading: isTemplateBlobLoading } = useQuery(
    ["datasets", "template", selectedDataset],
    () =>
      TemplateAdapter.dataset.getOne({
        datasetId: selectedDataset,
        companyId: claims.company.id!,
      }),
    {
      enabled: Boolean(selectedDataset) && Boolean(claims?.company?.id),
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    }
  );

  const handleDownloadTemplateClick = () => {
    if (!templateBlob) {
      toast.push({
        intent: "error",
        description: "Template not available!",
      });
      return;
    }

    const selectedDatasetName = datasets?.data.find(
      (d) => d.id === selectedDataset
    )?.name;
    const fileName =
      `${selectedDatasetName ? `${snakeCase(selectedDatasetName)}_` : ""}import_template`.toLowerCase();

    try {
      downloadBlob(fileName, templateBlob);
      toast.push({
        intent: "success",
        description: `Template ${fileName}.xlsx downloaded.`,
      });
    } catch (err) {
      toast.push({
        intent: "error",
        description: `Template failed to download.`,
      });
      ExceptionUtils.reportException(err, "error", {
        hint: "Failed to download import template for templated configuration",
      });
    }
  };

  const lastRegistrationRef = useRef<DatasetFileImportRegistrationType | null>(
    null
  );

  const formSubmissionMutation = useMutation({
    mutationFn: async (formValues: DataSetImportDialogFormType) => {
      await DataSetImportDialogForm.safeParseAsync(formValues);

      if (!selectedDataset || !claims) {
        throw new Error("A dataset must be selected to start an import");
      }

      const { transactionId } = await uploadFileForDatasetImport({
        file: formValues.file.pop()!,
        datasetId: selectedDataset,
        companyId: claims.company?.id,
        previousRegistration: lastRegistrationRef.current,
        onStatusChange: setStatus,
        onRegistered: (registration) => {
          lastRegistrationRef.current = registration;
        },
      });

      const transaction = await TransactionAdapter.getOne({
        id: transactionId,
        meta: { history: false },
      });

      if (!transaction?.data.length) {
        throw new Error("No transactions found for initiated transaction");
      }

      return transaction;
    },
    onSuccess: (transaction) => {
      lastRegistrationRef.current = null;
      setQueue(queue.insert(transaction.data[0]));
      onClose();
      toast.push({
        intent: "info",
        description: "Successfully started data import",
      });
      queryClient.invalidateQueries({
        queryKey: [Resources.TRANSACTION],
      });
      queryClient.invalidateQueries({ queryKey: [Resources.DATASET] });
    },
    onError: (err) => {
      console.error(err);
      toast.push({
        intent: "error",
        description: "Failed to start data import. Try again?",
      });
    },
    onSettled: () => {
      setStatus({ percentComplete: 0, label: "Ready" });
    },
  });

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Import Data"
      actionRow={[
        <Button
          key="confirm"
          variant="primary"
          onClick={form.handleSubmit((data) => {
            formSubmissionMutation.mutate(data);
          })}
          isLoading={formSubmissionMutation.isLoading}
        >
          Import
        </Button>,
      ]}
    >
      Choose the dataset you&apos;d like to upload to and select the import
      file.
      <Form {...form}>
        <DropdownInput
          name="dataset"
          label="Dataset"
          isRequired
          isLoading={areDatasetsLoading}
          isSortedAlphabetically
          placeholder="Select a dataset to import against"
          options={
            datasets?.data.map((set) => ({
              name: `${toTitleCaseString(set.name, { intent: "content" })} (${set.active ? "active" : "inactive"})`,
              id: set.id,
            })) ?? []
          }
          labelKey="name"
          valueKey="id"
          isFluid
          style={{ marginTop: 16 }}
        />
        <Button
          icon="download-simple"
          variant="outline"
          onClick={handleDownloadTemplateClick}
          isLoading={Boolean(
            areDatasetsLoading || (selectedDataset && isTemplateBlobLoading)
          )}
          disabled={!selectedDataset}
        >
          Download Import Template
        </Button>
        <FileInput
          name="file"
          label="Select file to upload"
          placeholder="Upload a .csv, .xls, .xlsx, or .json file"
          isRequired={true}
          rules={{
            required: "A file with data to import is required",
          }}
          acceptedFileTypes={acceptedMimeTypes}
          style={{ marginTop: 12 }}
          isDisabled={
            !selectedDataset ||
            form.formState.isSubmitting ||
            !!status?.percentComplete
          }
        />
      </Form>
    </Dialog>
  );
};
